import { BooleanFormField, ValidationFormField } from './form-utils'
import { Category, Employee } from '../api-clients/api-types'
import { capitalize } from './string-utils'
import { datePickerFormField } from '../components/form/validatable-date-picker'

export const surnameFormField = (value: string, required = true): ValidationFormField<string> => {
  return {
    value,
    variant: '',
    feedback: '',
    fieldName: 'surname',
    displayName: 'Etternavn',
    placeholder: 'Navnesen',
    validation: {
      errorMessage: 'Etternavn kan ikke ha tall',
      pattern: '^[\\D]*[*]?$',
      required,
    },
  }
}

export const firstNameFormField = (value: string, required = true): ValidationFormField<string> => {
  return {
    value,
    variant: '',
    feedback: '',
    fieldName: 'firstName',
    displayName: 'Fornavn',
    placeholder: 'Navn',
    validation: {
      errorMessage: 'Fornavn kan ikke ha tall',
      pattern: '^[\\D]*[*]?$',
      required,
    },
  }
}

export const dateOfBirthFormField = (
  value: Date | undefined,
  required = true
): ValidationFormField<Date | undefined> => {
  return datePickerFormField('dateOfBirth', 'Fødselsdato', value, required, undefined, true)
}

export const addressFormField = (value: string, required = true): ValidationFormField<string> => {
  return {
    value,
    variant: '',
    feedback: '',
    fieldName: 'addressLine',
    displayName: 'Adresse',
    validation: {
      errorMessage: 'Adresse er påkrevet',
      required,
    },
  }
}

export const postalCodeFormField = (
  value: string,
  required = true,
  town?: string
): ValidationFormField<string> => {
  const displayValue = town ? `${value}, ${town}` : value
  return {
    value,
    variant: '',
    feedback: '',
    fieldName: 'postalCode',
    displayName: 'Postnr.',
    placeholder: '1234',
    validation: {
      errorMessage: 'Kun 8 bokstaver (A-Z), tall og bindestrek tillatt',
      pattern: '^[A-Za-z0-9-]{0,8}$',
      required,
    },
    displayValue,
  }
}

export const productFormField = (value?: Category, required = false): ValidationFormField<string> => {
  return {
    value: value?.id || '',
    displayValue: capitalize(value?.name) || 'Ikke valgt',
    variant: '',
    feedback: '',
    fieldName: 'preferredProductId',
    displayName: 'Valør',
    validation: {
      required,
    },
  }
}

export const isFeePaidFormField = (inital: boolean | undefined): BooleanFormField => {
  const displayValue = inital ? 'Betalt' : 'Ikke betalt'
  return {
    value: inital || false,
    fieldName: 'isFeePaid',
    displayName: 'Egenandel',
    positiveLabel: 'Betalt',
    negativeLabel: 'Ikke betalt',
    displayValue,
  }
}

export const employmentTypeFormField = (
  employee?: Employee,
  required = false
): ValidationFormField<string | undefined> => {
  return {
    value: employee?.employmentType?.code || 'F',
    displayValue: employee?.employmentType?.description || 'Fast ansatt i stilling',
    draftValue: employee?.draftEmployee?.employmentType?.description,
    variant: '',
    feedback: '',
    fieldName: 'employmentTypeCode',
    displayName: 'Tjenesteforhold',
    validation: {
      required,
    },
  }
}

export const employeeNoFormField = (employee?: Employee, required = false): ValidationFormField<string> => {
  return {
    value: employee?.employeeNo || '',
    draftValue: employee?.draftEmployee?.employeeNo,
    variant: '',
    feedback: '',
    fieldName: 'employeeNo',
    displayName: 'Ansattnummer',
    validation: {
      errorMessage: 'Kun tall og bindestrek',
      pattern: '^[\\d-]*$',
      required,
    },
  }
}

export const employmentPercentageFormField = (employee?: Employee): ValidationFormField<string> => {
  return {
    value: employee ? employee?.employmentPercentage || '' : '100',
    draftValue: employee?.draftEmployee?.employmentPercentage,
    fieldName: 'employmentPercentage',
    displayName: 'Tilsattprosent',
    variant: '',
    feedback: '',
    validation: {
      pattern: '^$|^[0-9][0-9]?$|^100$',
      errorMessage: 'Tilsattprosent må være mellom 0 og 100',
    },
  }
}

export const paidPercentageFormField = (employee?: Employee): ValidationFormField<string> => {
  return {
    value: employee ? employee?.paidPercentage?.toString() || '' : '100',
    draftValue: employee?.draftEmployee?.paidPercentage?.toString(),
    fieldName: 'paidPercentage',
    displayName: 'Lønnsprosent',
    variant: '',
    feedback: '',
    validation: {
      pattern: '^$|^[0-9][0-9]?$|^100$',
      errorMessage: 'Lønnsprosent må være mellom 0 og 100',
    },
  }
}

export const payTypeFormField = (
  employee?: Employee,
  required = false,
  locked = false
): ValidationFormField<string | undefined> => {
  return {
    value: employee?.sensitiveInfo?.payType?.code || '',
    displayValue: employee?.sensitiveInfo?.payType?.code
      ? `${employee?.sensitiveInfo?.payType?.code} - ${employee?.sensitiveInfo?.payType?.description}`
      : '',
    draftValue:
      employee?.sensitiveInfo?.draftEmployee?.payType &&
      `${employee?.sensitiveInfo?.draftEmployee?.payType?.code} - ${employee?.sensitiveInfo?.draftEmployee?.payType?.description}`,
    variant: '',
    feedback: '',
    fieldName: 'payType',
    displayName: 'Lønnstype',
    locked,
    isSensitive: true,
    validation: {
      required,
    },
  }
}

export const disabilityGradeFormField = (employee?: Employee, locked = false): ValidationFormField<string> => {
  return {
    value: employee?.sensitiveInfo?.disabilityGrade?.toString() || '',
    draftValue: employee?.sensitiveInfo?.draftEmployee?.disabilityGrade?.toString(),
    fieldName: 'disabilityGrade',
    displayName: 'Uførhetsandel',
    locked,
    isSensitive: true,
    variant: '',
    feedback: '',
    validation: {
      pattern: '^$|^[0-9][0-9]?$|^100$',
      errorMessage: 'Uførhetsandel må være mellom 0 og 100',
    },
  }
}

export const disabilityProofRegisteredFormField = (value?: boolean, locked = false): BooleanFormField => {
  return {
    value: value || false,
    positiveLabel: 'Motatt',
    negativeLabel: 'Ikke motatt',
    displayValue: value ? 'Motatt' : 'Ikke motatt',
    fieldName: 'disabilityProofRegistered',
    displayName: 'Utførhetsattest',
    locked,
    isSensitive: true,
  }
}

export const hireStartDateFormField = (employee?: Employee, required = true) => {
  return datePickerFormField(
    'hireStartDate',
    'Ansattdato',
    employee?.hireStartDate,
    required,
    employee?.draftEmployee?.hireStartDate
  )
}

export const employmentStartDateFormField = (employee?: Employee, required = true) => {
  return datePickerFormField(
    'employmentPercentageDate',
    'Stilling fra',
    employee?.employmentPercentageDate,
    required,
    employee?.draftEmployee?.employmentPercentageDate
  )
}

export const hireEndCauseFormField = (
  employee?: Employee,
  locked = false
): ValidationFormField<string | undefined> => {
  return {
    value: employee?.sensitiveInfo?.hireEndCause?.code,
    displayValue: employee?.sensitiveInfo?.hireEndCause?.code
      ? `${employee?.sensitiveInfo?.hireEndCause?.code} - ${employee?.sensitiveInfo?.hireEndCause?.description}`
      : '',
    draftValue:
      employee?.sensitiveInfo?.draftEmployee?.hireEndCause &&
      `${employee?.sensitiveInfo?.draftEmployee?.hireEndCause?.code} - ${employee?.sensitiveInfo?.draftEmployee?.hireEndCause?.description}`,
    variant: '',
    feedback: '',
    fieldName: 'hireEndCause',
    displayName: 'Sluttårsak',
    locked,
    isSensitive: true,
    validation: {
      required: false,
    },
  }
}

export const hireEndDateFormField = (employee?: Employee, required = true) => {
  return datePickerFormField(
    'hireEndDate',
    'Sluttdato',
    employee?.hireEndDate,
    required,
    employee?.draftEmployee?.hireEndDate
  )
}

export const leaveFromDateFormField = (employee?: Employee, required = true) => {
  return datePickerFormField(
    'leaveFromDate',
    'Permisjon fra',
    employee?.leaveFromDate,
    required,
    employee?.draftEmployee?.leaveFromDate
  )
}

export const ticketTypeFormField = (
  value: string,
  displayValue?: string,
  required = false,
  locked = false
): ValidationFormField<string> => {
  return {
    value,
    displayValue: displayValue || '',
    variant: '',
    feedback: '',
    fieldName: 'ticketType',
    displayName: 'Billettype',
    locked,
    isSensitive: false,
    validation: {
      required,
    },
  }
}
